import { useVehicleMetricsClient, VehicleMetricsService } from '@/api-proxy/useProxiedClient'
import ErrorState from '@/components/Common/ErrorState'
import { DIAGNOSTICS_LOGS_REFETCH_INTERVAL_MS } from '@/constants'
import { Button, Card, Code, Group, LoadingOverlay, ScrollArea, Tabs, Text } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import { FaSync } from 'react-icons/fa'
import { useDiagnosticsQuery } from './useDiagnosticsQuery'
import { isLogText } from './validation'

const METRICS_SERVICES: Record<'cs-metrics' | 'cu-metrics', VehicleMetricsService> = {
  'cs-metrics': 'csMetrics',
  'cu-metrics': 'cuMetrics',
}

/**
 * Any path outside the proxy's own routes works here, because the proxy rewrites requests of the metrics
 * services to /metrics on the vehicle. Requesting /metrics directly would hit the proxy's own Prometheus
 * endpoint, which is guarded by internal credentials and answers 401.
 */
const VEHICLE_METRICS_PATH = '/vehicle-metrics'

const MetricsPanel = ({
  value,
  title,
  source,
}: {
  value: 'cs-metrics' | 'cu-metrics'
  title: string
  source: string
}) => {
  const { t } = useTranslation(['diagnostics', 'common'])

  const client = useVehicleMetricsClient(source, METRICS_SERVICES[value])

  const { data, isLoading, isFetching, isError, refetch } = useDiagnosticsQuery({
    queryKey: ['diagnostics-metrics', source, value],
    queryFn: async () => {
      const { data } = await client.get({ url: VEHICLE_METRICS_PATH, parseAs: 'text', throwOnError: true })
      return data
    },
    refetchInterval: DIAGNOSTICS_LOGS_REFETCH_INTERVAL_MS,
  })

  const metrics = isLogText(data) ? data : undefined
  const hasInvalidResponse = !isError && data !== undefined && metrics === undefined

  return (
    <Tabs.Panel value={value}>
      <Card shadow="sm" p="lg" radius="md" withBorder mt="lg" pos="relative">
        <LoadingOverlay visible={isLoading} />
        <Group justify="space-between" align="center" mb="md">
          <Text fw={500}>{title}</Text>
          <Button
            variant="light"
            size="xs"
            leftSection={<FaSync size={12} />}
            onClick={() => refetch()}
            loading={isFetching}
          >
            {t('refresh')}
          </Button>
        </Group>

        {isError && (
          <ErrorState
            title={t('metricsFetchFailed')}
            message={t('metricsFetchFailedMessage', { source: source.toUpperCase() })}
            showBackButton={false}
            width="100%"
          />
        )}

        {hasInvalidResponse && (
          <ErrorState
            title={t('unexpectedResponse')}
            message={t('metricsUnexpectedResponseMessage', { source: source.toUpperCase() })}
            showBackButton={false}
            width="100%"
          />
        )}

        {!isError &&
          !hasInvalidResponse &&
          (metrics ? (
            <ScrollArea h={600} type="auto">
              <Code block style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                {metrics}
              </Code>
            </ScrollArea>
          ) : (
            !isLoading && <Text size="sm">{t('noMetrics')}</Text>
          ))}
      </Card>
    </Tabs.Panel>
  )
}

export default MetricsPanel
